import { getOrderProductUniqueIdentifier } from '../../../../utils/getOrderProductUniqueIdentifier';
import {
  OrderCalculationHeaders,
  OrderHeadersGroupBy,
  OrderHeadersGroupByEnum,
  OrderProduct,
  PriceCompositionTotals,
  Section,
  SectionPendencyEnum,
} from '../orderProducts.types';
import { OrderProductForGridContainer } from '../product/grid/components/orderProductGrid/components/grid/types/orderProductGridContainer.types';
import {
  GetHeaderTotalsProps,
  GetParentGroupSectionsParams,
  ParentGroupHeaderPlacement,
  ParentGroupSections,
  ResolveParentGroupHeaderPlacementParams,
  SectionTotals,
} from './orderProductsSection.types';

export const getOrderProductKey = (product: OrderProduct | OrderProductForGridContainer) => `
  ${product.productColorId}-
  ${product.productId}-
  ${product.availability}-
  ${(product as OrderProduct).deliveryWindow?.id || (product as OrderProductForGridContainer).deliveryWindowId || ''}
`;

function removeSpaces(str?: string) {
  return str?.replace(/\s+/g, '').replaceAll('--', '-');
}

export const normalizeSectionIdentifier = (identifier?: string) => {
  return identifier?.split('_')?.join('-');
};

export const getHeaderTotals = ({
  headerId,
  headers,
  sectionAvailability,
  sectionWithPendencies,
  productCode,
  groupIdentifier,
}: GetHeaderTotalsProps) => {
  if (!headers) {
    return;
  }

  if (groupIdentifier) {
    const identifier = removeSpaces(normalizeSectionIdentifier(groupIdentifier));
    const found = headers.sections.find((item) => {
      return removeSpaces(item.multipleIdentifier) === identifier;
    });
    return found;
  }

  switch (headers.type) {
    case OrderHeadersGroupByEnum.DELIVERY_WINDOW:
      return headers.sections.find((headerSection) => {
        if (
          sectionAvailability === 'READY_FOR_DELIVERY' &&
          headerSection.availability === 'READY_FOR_DELIVERY'
        ) {
          return true;
        }
        if (sectionAvailability === 'EMPTY' && headerSection.availability === 'EMPTY') {
          return true;
        }

        return (
          headerSection.deliveryWindowId === headerId &&
          sectionAvailability === headerSection.availability
        );
      });
    case OrderHeadersGroupByEnum.CATEGORY:
      return headers.sections.find((section) => section.categoryId === headerId);
    case OrderHeadersGroupByEnum.COLLECTION:
      return headers.sections.find((section) => section.collectionId === headerId);
    case OrderHeadersGroupByEnum.SUB_CATEGORY:
      return headers.sections.find((section) => section.subCategoryId === headerId);
    case OrderHeadersGroupByEnum.COLOR_CLASSIFICATION_TYPE:
      return headers.sections.find((section) => section.classificationId === headerId);
    case OrderHeadersGroupByEnum.PENDENCY:
      return headers.sections.find((section) => section.withPendencies === sectionWithPendencies);
    case OrderHeadersGroupByEnum.PRODUCT:
      return headers.sections.find((section) => section.productCode === productCode);
    case OrderHeadersGroupByEnum.DEFAULT:
    default:
      return headers.sections[0];
  }
};

export const getProductsByKitId = (orderProducts: OrderProduct[]) => {
  const productsByKitId = new Map<string, OrderProduct[]>();

  orderProducts.forEach((orderProduct) => {
    const kitId = orderProduct.kit?.id;

    if (!kitId) {
      return;
    }

    const kitProducts = productsByKitId.get(kitId);

    if (kitProducts) {
      kitProducts.push(orderProduct);
      return;
    }

    productsByKitId.set(kitId, [orderProduct]);
  });

  return productsByKitId;
};

interface GetSectionSelectionKeysParams {
  sectionProducts: OrderProduct[];
  productsByKitId: Map<string, OrderProduct[]>;
}

const getSectionSelectionKeys = ({
  sectionProducts,
  productsByKitId,
}: GetSectionSelectionKeysParams) => {
  const keys = new Set<string>();

  sectionProducts.forEach((product) => {
    const kitId = product.kit?.id;

    if (kitId) {
      productsByKitId
        .get(kitId)
        ?.forEach((orderProduct) => keys.add(getOrderProductUniqueIdentifier(orderProduct)));
    }

    keys.add(getOrderProductUniqueIdentifier(product));
  });

  return keys;
};

interface GetSectionSelectionParams extends GetSectionSelectionKeysParams {
  selectedItems: Set<string>;
}

export const getSectionSelection = ({
  sectionProducts,
  productsByKitId,
  selectedItems,
}: GetSectionSelectionParams) => {
  const keys = getSectionSelectionKeys({ sectionProducts, productsByKitId });
  const selectedCount = [...keys].filter((key) => selectedItems.has(key)).length;
  const isSelected = keys.size > 0 && selectedCount === keys.size;

  return { keys, isSelected, isIndeterminate: selectedCount > 0 && !isSelected };
};

const isOrderHeadersGroupBy = (value: string | null): value is OrderHeadersGroupBy =>
  !!value && Object.values(OrderHeadersGroupByEnum).includes(value as OrderHeadersGroupByEnum);

export const getGroupBySearchParam = (search: string): OrderHeadersGroupBy => {
  const groupBy = new URLSearchParams(search).get('GROUP_BY');

  return isOrderHeadersGroupBy(groupBy) ? groupBy : OrderHeadersGroupByEnum.DEFAULT;
};

interface GetSectionTotalsParams {
  section: Section;
  headers?: OrderCalculationHeaders;
  groupBy: OrderHeadersGroupByEnum;
}

export const getSectionTotals = ({ section, headers, groupBy }: GetSectionTotalsParams) => {
  const headerTotals = getHeaderTotals({
    headers,
    headerId: getHeaderId(section, groupBy),
    sectionAvailability: section?.availability,
    sectionWithPendencies: section?.pendency === SectionPendencyEnum.WITH_PENDENCIES,
    productCode: section?.products?.[0]?.code || '',
    groupIdentifier: section?.identifier,
  });

  return {
    itemsQuantity: section?.productsCount ?? 0,
    piecesQuantity: headerTotals?.pieces ?? 0,
    total: headerTotals?.total ?? 0,
    incomeStatus: headerTotals?.incomeStatus,
    priceCompositionTotals: headerTotals?.priceCompositionTotals ?? [],
  };
};

export const sumSectionsTotals = (sectionsTotals: SectionTotals[]): SectionTotals => {
  const priceCompositionTotalsById = new Map<string, PriceCompositionTotals>();

  sectionsTotals.forEach(({ priceCompositionTotals }) => {
    priceCompositionTotals.forEach((composition) => {
      const currentTotalValue = priceCompositionTotalsById.get(composition.id)?.totalValue ?? 0;

      priceCompositionTotalsById.set(composition.id, {
        ...composition,
        totalValue: currentTotalValue + composition.totalValue,
      });
    });
  });

  const quantities = sectionsTotals.reduce(
    (accumulated, sectionTotals) => ({
      itemsQuantity: accumulated.itemsQuantity + sectionTotals.itemsQuantity,
      piecesQuantity: accumulated.piecesQuantity + sectionTotals.piecesQuantity,
      total: accumulated.total + sectionTotals.total,
    }),
    { itemsQuantity: 0, piecesQuantity: 0, total: 0 },
  );

  return { ...quantities, priceCompositionTotals: [...priceCompositionTotalsById.values()] };
};

export const getParentGroupSections = ({
  sections,
  parentGroupKey,
  collapsedParentGroupKeys,
}: GetParentGroupSectionsParams): ParentGroupSections => {
  const parentGroupSections = sections.filter((item) => item.parentGroupKey === parentGroupKey);
  const isCollapsed = collapsedParentGroupKeys.includes(parentGroupKey);

  return {
    sections: parentGroupSections,
    isExpanded: !isCollapsed || parentGroupSections.some((item) => item.isExpanded),
  };
};

export const resolveParentGroupHeaderPlacement = ({
  section,
  parentGroupSections,
  hasStickyParentGroupSlot,
  isParentGroupExpanded,
}: ResolveParentGroupHeaderPlacementParams) => {
  if (!section?.parentGroupKey) {
    return ParentGroupHeaderPlacement.NONE;
  }

  if (parentGroupSections.find((item) => item.products.length > 0) === section) {
    return ParentGroupHeaderPlacement.INLINE;
  }

  if (hasStickyParentGroupSlot && isParentGroupExpanded) {
    return ParentGroupHeaderPlacement.STICKY_SLOT;
  }

  return ParentGroupHeaderPlacement.NONE;
};

export const getHeaderId = (
  section: Section,
  view: OrderHeadersGroupByEnum,
): string | undefined | null => {
  switch (view) {
    case OrderHeadersGroupByEnum.DELIVERY_WINDOW:
      return section.deliveryWindow?.id;
    case OrderHeadersGroupByEnum.COLLECTION:
      return section.collection?.id;
    case OrderHeadersGroupByEnum.CATEGORY:
      return section.category?.id;
    case OrderHeadersGroupByEnum.SUB_CATEGORY:
      return section.subCategory?.id;
    case OrderHeadersGroupByEnum.COLOR_CLASSIFICATION_TYPE:
      return section.colorClassification?.id;
    default:
      return null;
  }
};
